// src/services/adminApi.js - COMPLETE VERSION with Support Tickets
import api from './api';

export const adminApi = {
  // ============================================
  // DASHBOARD
  // ============================================
  getOverview: () => api.get('/api/admin/dashboard/overview').then(r => r.data),

  // Every sidebar badge count in one request — see app/routes/admin/nav_badges.py.
  getNavBadges: () => api.get('/api/admin/nav-badges').then(r => r.data),

  // ============================================
  // DISCOUNT CODES (creator-issued product coupons — not the creator
  // referral programme). Read-mostly: admins can pause, not edit.
  // ============================================
  getDiscountCodes: (params = {}) =>
    api.get('/api/admin/discount-codes', { params }).then(r => r.data),

  getDiscountCodeDetail: (creatorId, code) =>
    api.get(`/api/admin/discount-codes/${creatorId}/${encodeURIComponent(code)}`).then(r => r.data),

  setDiscountCodeStatus: (creatorId, code, active) =>
    api.patch(`/api/admin/discount-codes/${creatorId}/${encodeURIComponent(code)}/status`, null, {
      params: { active },
    }).then(r => r.data),


  // Platform-wide inbound attribution — which apps and sites send us visitors.
  getTrafficSources: (days = 30) =>
    api.get('/api/admin/traffic-sources', { params: { days } }).then(r => r.data),
  getStatistics: () => api.get('/api/admin/statistics/overview').then(r => r.data),

  // Traffic, logins and spend with the reasoning attached. Served from a
  // 6-hourly cache because building it costs real money (Cost Explorer bills
  // per request, Logs Insights per GB scanned) — pass refresh=true only on a
  // deliberate click, never on page load. The rebuild runs a log query, so it
  // needs a much longer timeout than the shared default.
  getApiMetrics: (refresh = false) =>
    api.get('/api/admin/api-metrics', { params: { refresh }, timeout: 120000 }).then(r => r.data),

  // Unique-creator engagement per dashboard feature card ("how many people
  // have actually tried Events, Review My Work, the storefront builder...").
  getFeatureUsage: () => api.get('/api/admin/feature-usage').then(r => r.data),

  // Reconciles every creator's stored balance against the sales behind it.
  // On-demand rather than auto-loading: it scans the sales table (~4s), and
  // nothing here needs to be live on every dashboard render.
  getEarningsIntegrity: () =>
    api.get('/api/admin/earnings-integrity', { timeout: 120000 }).then(r => r.data),

  // Resets one (or, with creatorId omitted, every) drifted creator's stored
  // total_earnings/total_sales to match the sum of their successful sales.
  // apply=false previews the write; apply=true performs it.
  repairEarningsDrift: (creatorId, apply = false) =>
    api.post('/api/admin/earnings-integrity/repair', null, {
      params: { creator_id: creatorId || undefined, apply },
      timeout: 120000,
    }).then(r => r.data),

  // ============================================
  // CREATORS
  // ============================================
  getCreators: (params = {}) =>
    api.get('/api/admin/creators', { params }).then(r => r.data),

  getCreatorById: (creatorId) =>
    api.get(`/api/admin/creators/${creatorId}`).then(r => r.data),

  getCreatorTrafficSources: (creatorId, days = 30) =>
    api.get(`/api/admin/creators/${creatorId}/traffic-sources`, { params: { days } }).then(r => r.data),

  updateCreatorStatus: (creatorId, data) =>
    api.patch(`/api/admin/creators/${creatorId}/status`, data).then(r => r.data),

  setTestAccountFlag: (creatorId, isTest) =>
    api.patch(`/api/admin/creators/${creatorId}/test-flag`, { is_test_account: isTest }).then(r => r.data),

  // Creator's own write actions, newest first. { activity, next_cursor }.
  getCreatorActivity: (creatorId, { category = '', cursor = null, limit = 50 } = {}) =>
    api.get(`/api/admin/creators/${creatorId}/activity`, {
      params: { limit, ...(category ? { category } : {}), ...(cursor ? { cursor } : {}) },
    }).then(r => r.data),

  // Every creator's write actions, platform-wide (audit log → Creator Activity).
  getCreatorActivityFeed: ({ cursor = null, limit = 50, ...filters } = {}) =>
    api.get('/api/admin/creator-activity', {
      params: {
        limit,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== false && v != null)),
        ...(cursor ? { cursor } : {}),
      },
    }).then(r => r.data),

  setGuardianManaged: (creatorId, managed) =>
    api.patch(`/api/admin/creators/${creatorId}/guardian-managed`, { guardian_managed: managed }).then(r => r.data),

  setCreatorFlag: (creatorId, isFlagged) =>
    api.patch(`/api/admin/creators/${creatorId}/flag`, { is_flagged: isFlagged }).then(r => r.data),

  changeCreatorEmail: (creatorId, email) =>
    api.patch(`/api/admin/creators/${creatorId}/email`, { email }).then(r => r.data),

  deleteTestCreator: (creatorId) =>
    api.delete(`/api/admin/creators/${creatorId}`).then(r => r.data),

  // Multi-select "Delete selected" in Creator Management's test-account view.
  // Each id is independently validated server-side (must already be flagged
  // is_test_account=true) — one bad id in the batch doesn't fail the rest.
  bulkDeleteTestCreators: (creatorIds) =>
    api.post('/api/admin/creators/bulk-delete-test-accounts', { creator_ids: creatorIds }).then(r => r.data),

  resendDownloadLink: (sale_id) =>
    api.post(`/api/admin/sales/${sale_id}/resend-download`).then(r => r.data),

  // Per-download IP/geo/device log for a single sale
  getSaleDownloadLog: (sale_id) =>
    api.get(`/api/admin/sales/${sale_id}/downloads`).then(r => r.data),

  sendInvoice: (sale_id) =>
    api.post(`/api/admin/sales/${sale_id}/send-invoice`).then(r => r.data),

  revokeDownloadToken: (sale_id) =>
    api.post(`/api/admin/sales/${sale_id}/revoke-token`).then(r => r.data),

  updateSale: (sale_id, data) => api.patch(`/api/admin/sales/${sale_id}`, data).then(r => r.data),

  // ============================================
  // EVENTS / BOOKINGS
  // ============================================
  getAllEvents: (params = {}) =>
    api.get('/api/admin/events', { params }).then(r => r.data),

  getEventDetail: (creatorId, eventId) =>
    api.get(`/api/admin/events/${creatorId}/${eventId}`).then(r => r.data),

  // Edit/delete a creator's event on their behalf — same write path and
  // has-bookings delete guard as the creator's own event editor.
  updateEventAdmin: (creatorId, eventId, data) =>
    api.patch(`/api/admin/events/${creatorId}/${eventId}`, data).then(r => r.data),

  deleteEventAdmin: (creatorId, eventId) =>
    api.delete(`/api/admin/events/${creatorId}/${eventId}`).then(r => r.data),

  searchBookings: (params = {}) =>
    api.get('/api/admin/bookings', { params }).then(r => r.data),

  getBookingDetail: (saleId) =>
    api.get(`/api/admin/bookings/${saleId}`).then(r => r.data),

  checkinBookingAdmin: (saleId, undo = false) =>
    api.post(`/api/admin/bookings/${saleId}/checkin`, { undo }).then(r => r.data),

  resendTicketAdmin: (saleId) =>
    api.post(`/api/admin/bookings/${saleId}/resend-ticket`).then(r => r.data),

  reissueTicketAdmin: (saleId, force = false) =>
    api.post(`/api/admin/bookings/${saleId}/reissue-ticket`, { force }).then(r => r.data),

  // ── Event cancellation requests ──────────────────────────────────────────────
  getCancellations: (params = {}) =>
    api.get('/api/admin/cancellations', { params }).then(r => r.data),

  getCancellationDetail: (requestId) =>
    api.get(`/api/admin/cancellations/${requestId}`).then(r => r.data),

  settleCancellationDue: (requestId, notes) =>
    api.post(`/api/admin/cancellations/${requestId}/settle-due`, { notes }).then(r => r.data),

  approveCancellation: (requestId) =>
    api.post(`/api/admin/cancellations/${requestId}/approve`).then(r => r.data),

  rejectCancellation: (requestId, notes) =>
    api.post(`/api/admin/cancellations/${requestId}/reject`, { notes }).then(r => r.data),

  // ── PayU Reconciliation ──────────────────────────────────────────────────────

  // Cross-check all sale txnids against PayU and update statuses/reasons.
  // No emails or links sent (backend always sync-only).
  // Returns: { synced_at, updated, admin_only (count), payu_only (count),
  //            summary: { total_scanned, verified_with_payu, status_changed, admin_only_count },
  //            updated_records: [...], admin_only_records: [...] }
  // One external PayU call per sale in the table — genuinely minutes, not
  // seconds, at current volume. Overrides the shared 60s default rather than
  // raising it for every other admin call.
  syncWithPayU: () =>
    api.post('/api/admin/payu/sync', {}, { timeout: 300000 }).then(r => r.data),

  // Returns cached results from last sync.
  // admin_only: sales in DB not found in PayU (abandoned checkouts / webhook failures)
  // payu_only:  PayU txns not in DB (from last check-mihpayids run)
  getPayUUnmatched: () =>
    api.get('/api/admin/payu/unmatched').then(r => r.data),

  // Given a list of mihpayids from PayU's transaction CSV export,
  // returns which ones are missing from our DB (payu_only).
  // mihpayids: string[]
  checkMihpayids: (mihpayids) =>
    api.post('/api/admin/payu/check-mihpayids', { mihpayids }).then(r => r.data),

  // Create a new sale record from an orphaned PayU transaction.
  // If payment_status === 'success', credits creator wallet + emails buyer download link.
  createSaleFromPayU: (data) =>
    api.post('/api/admin/sales/from-payu', data).then(r => r.data),
  // ============================================
  // BANK VERIFICATION (CRITICAL - WAS MISSING)
  // ============================================
  getPendingBankVerifications: () =>
    api.get('/api/admin/bank-accounts/pending').then(r => r.data),

  getAllBankAccounts: (params = {}) =>
    api.get('/api/admin/bank-accounts', { params }).then(r => r.data),

  getBankVerificationStats: () =>
    api.get('/api/admin/bank-accounts/stats').then(r => r.data),

  // Creators who have NO bank account AND NO UPI ID
  getCreatorsWithNoPayout: () =>
    api.get('/api/admin/creators/no-payout-method').then(r => r.data),

  verifyBankAccount: (creatorId, bankId, data) =>
    api.post(`/api/admin/bank-accounts/${creatorId}/${bankId}/verify`, {...data,bank_id:bankId}).then(r => r.data),

  // Deletes the payout method outright (reason is required and audit-logged).
  deleteBankAccount: (creatorId, bankId, reason) =>
    api.delete(`/api/admin/bank-accounts/${creatorId}/${bankId}`, { params: { reason } }).then(r => r.data),

  // Asks PayU who the UPI ID / bank account belongs to. Fetch only — never
  // changes verification_status. targets: ['upi'] and/or ['bank'] ('bank' is
  // a paid penny drop). Returns { payu_lookup: { upi?: {...}, bank?: {...} } }.
  lookupPayoutMethod: (creatorId, bankId, targets) =>
    api.post(`/api/admin/bank-accounts/${creatorId}/${bankId}/lookup`, { targets }).then(r => r.data),

  // Approve-only, no notes — see the route's docstring for why bulk never rejects.
  bulkVerifyBankAccounts: (accounts) =>
    api.post('/api/admin/bank-accounts/bulk-verify', { accounts }).then(r => r.data),

  // ============================================
  // PAGES
  // ============================================
  getPages: (params = {}) =>
    api.get('/api/admin/pages', { params }).then(r => r.data),

  // One-time repair for pages saved before GSI5PK='PAGE' existed — see
  // app/routes/admin/pages.py's backfill_pages_index docstring. Idempotent.
  backfillPagesIndex: () =>
    api.post('/api/admin/pages/backfill-index').then(r => r.data),

  // ============================================
  // ASSETS
  // ============================================
  getAssets: (params = {}) =>
    api.get('/api/admin/assets', { params }).then(r => r.data),

  // One-time repair for sales_badge_label values from before it was a fixed
  // dropdown — see app/routes/admin/assets.py's backfill_sales_badge_labels
  // docstring. Idempotent.
  backfillSalesBadgeLabels: () =>
    api.post('/api/admin/assets/backfill-sales-badge-labels').then(r => r.data),

  getAssetStats: (assetId) =>
    api.get(`/api/admin/assets/${assetId}/stats`).then(r => r.data),

  // Per-download IP/geo/device log for a single asset
  getAssetDownloadLog: (assetId, params = {}) =>
    api.get(`/api/admin/assets/${assetId}/downloads`, { params }).then(r => r.data),

  updateAssetStatus: (assetId, data) =>
    api.patch(`/api/admin/assets/${assetId}/status`, data).then(r => r.data),

  setAssetNsfwFlag: (assetId, flagged) =>
    api.patch(`/api/admin/assets/${assetId}/nsfw-flag`, { nsfw_flag: flagged }).then(r => r.data),

  // Buyer-facing "AI generated" disclosure on the /m/:slug page. Sent through
  // the full admin-edit route (multipart) so it needs no new API Gateway route.
  setAssetAiGenerated: (assetId, aiGenerated) => {
    const form = new FormData();
    form.append('ai_generated', aiGenerated ? 'true' : 'false');
    return api.patch(`/api/admin/assets/${assetId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  // ============================================
  // UPLOAD TRACKING & ASSISTANCE
  // ============================================

  // Every asset-upload attempt (presign / direct-to-S3 / finalize), across
  // creator self-uploads, admin-on-behalf-of uploads, and link-based uploads.
  getUploadLogs: (params = {}) =>
    api.get('/api/admin/upload-logs', { params }).then(r => r.data),

  purgeUploadLogs: () =>
    api.delete('/api/admin/upload-logs/purge').then(r => r.data),

  // Admin uploads a digital asset directly on behalf of a creator.
  //
  // The file goes browser -> S3 via a presigned POST, NOT through this API.
  // Sending it as multipart meant every upload above ~10MB was rejected by
  // API Gateway with a bare 413 before the handler ran — while the handler
  // itself believed it accepted 200MB.
  presignAssetForCreator: (creatorId, body) =>
    api.post(`/api/admin/creators/${creatorId}/assets/presign`, body).then(r => r.data),

  createAssetForCreator: (creatorId, formData) =>
    api.post(`/api/admin/creators/${creatorId}/assets`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),

  /** Presign, PUT the bytes straight to S3, then create the record from URLs. */
  uploadAssetForCreator: async (creatorId, { file, thumbnail, fields, onStage }) => {
    const toS3 = async (theFile, uploadType) => {
      onStage?.(uploadType === 'asset' ? 'Uploading file…' : 'Uploading thumbnail…');
      const presigned = await adminApi.presignAssetForCreator(creatorId, {
        file_name: theFile.name,
        file_type: theFile.type || null,
        file_size: theFile.size,
        upload_type: uploadType,
      });

      const s3Form = new FormData();
      Object.entries(presigned.upload_fields).forEach(([k, v]) => s3Form.append(k, v));
      // The file part must come LAST — S3 ignores anything after it.
      s3Form.append('file', theFile);

      const res = await fetch(presigned.upload_url, { method: 'POST', body: s3Form });
      if (!res.ok) throw new Error(`Upload failed (${res.status}). Please try again.`);
      return presigned.file_url;
    };

    const fileUrl = await toS3(file, 'asset');
    const thumbUrl = thumbnail ? await toS3(thumbnail, 'thumbnail') : '';

    onStage?.('Creating the listing…');
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    fd.append('file_url', fileUrl);
    fd.append('file_name', file.name);
    fd.append('file_size', String(file.size));
    if (thumbUrl) fd.append('thumbnail_url', thumbUrl);

    return adminApi.createAssetForCreator(creatorId, fd);
  },

  // One-time expiring links the creator can use to upload themselves, no login needed
  createUploadLink: (creatorId, data = {}) =>
    api.post(`/api/admin/creators/${creatorId}/upload-links`, data).then(r => r.data),

  getUploadLinks: (params = {}) =>
    api.get('/api/admin/upload-links', { params }).then(r => r.data),

  revokeUploadLink: (linkId) =>
    api.delete(`/api/admin/upload-links/${linkId}`).then(r => r.data),

  // ============================================
  // SALES
  // ============================================
  // `config` lets a caller override the request (e.g. a longer timeout for a
  // wide date-range export that scans more of the sales table than the 60s
  // default comfortably allows).
  getSales: (params = {}, config = {}) =>
    api.get('/api/admin/sales', { params, ...config }).then(r => r.data),

  // ============================================
  // PAYOUTS - COMPLETE FLOW
  // ============================================
  
  // Step 1: View eligible creators (Friday payout generation)
  getPendingPayouts: () =>
    api.get('/api/admin/payouts/pending').then(r => r.data),

  // Step 2: Create batch for Friday payout
  createPayoutBatch: (data) =>
    api.post('/api/admin/payouts/create-batch', data).then(r => r.data),

  // Step 3: View all batches
  getPayoutBatches: (params = {}) =>
    api.get('/api/admin/payouts/batches', { params }).then(r => r.data),

  // Step 4: Get batch details
  getBatchDetails: (batchId) =>
    api.get(`/api/admin/payouts/batch/${batchId}`).then(r => r.data),

  // Step 5: Process batch (initiate payments)
  processBatch: (batchId) =>
    api.post(`/api/admin/payouts/process-batch/${batchId}`).then(r => r.data),

  // Step 6: Mark individual payout as complete (after bank confirmation)
  markPayoutComplete: async(payoutId, data) => {
    const formData = new FormData();
    formData.append('transaction_ref', (data.transaction_ref.toString()));
    if (data.notes) formData.append('notes', data.notes);
    return await api.post(`/api/admin/payouts/${payoutId}/complete`, formData).then(r => r.data);
  },

  // Step 7: Mark payout as failed
  markPayoutFailed: (payoutId, data) => {
    const formData = new FormData();
    formData.append('failure_reason', data.failure_reason);
    return api.post(`/api/admin/payouts/${payoutId}/fail`, formData).then(r => r.data);
  },

  // Step 8: Retry failed payout
  retryPayout: (payoutId) =>
    api.post(`/api/admin/payouts/${payoutId}/retry`).then(r => r.data),

  manualPayout: (payoutId) => api.post(`/api/admin/payouts/${payoutId}/process`).then(r => r.data),

  // Pay a single eligible creator immediately and record it (creates a completed payout)
  createManualPayout: (data) =>
    api.post('/api/admin/payouts/manual', data).then(r => r.data),

  // View payout history
  getPayoutHistory: (params = {}) =>
    api.get('/api/admin/payouts/history', { params }).then(r => r.data),

  // Per-creator lifetime earnings vs. actually-disbursed payouts, recomputed
  // from source sale/payout records (not the cached running-total fields) —
  // catches over/under-disbursement. Filters: search, date_from, date_to
  // (YYYY-MM-DD, restricts to creators with payout activity in that window),
  // only_flagged (over-disbursed only), only_mismatch (recomputed vs cached
  // "current payout amount" disagree), limit/offset.
  getPayoutReconciliation: (params = {}) =>
    api.get('/api/admin/payouts/reconciliation', { params }).then(r => r.data),

  // View payout audit trail
  getPayoutEvents: (payoutId) =>
    api.get(`/api/admin/payouts/${payoutId}/events`).then(r => r.data),

  // ============================================
  // REPORTS
  // ============================================
  getRevenueReport: (params = {}) =>
    api.get('/api/admin/reports/revenue', { params }).then(r => r.data),

  getTopPerformers: (params = {}) =>
    api.get('/api/admin/reports/top-performers', { params }).then(r => r.data),

  getGeoAnalytics: (params = {}) =>
    api.get('/api/admin/geo-analytics', { params }).then(r => r.data),

  // ============================================
  // PLATFORM SETTINGS (WITH ENFORCEMENT)
  // ============================================
  getSettings: () =>
    api.get('/api/admin/settings').then(r => r.data),

  updateSettings: (data) =>
    api.patch('/api/admin/settings', data).then(r => r.data),

  resetSettings: () =>
    api.post('/api/admin/settings/reset-defaults').then(r => r.data),

  // ============================================
  // AUDIT & LOGGING
  // ============================================
  getAuditLog: (params = {}) =>
    api.get('/api/admin/audit-log', { params }).then(r => r.data),

  createAuditEntry: (data) =>
    api.post('/api/admin/audit-log', data).then(r => r.data),

  getApiTrail: (params = {}) =>
    api.get('/api/admin/api-trail', { params }).then(r => r.data),

  // Creator-facing error / issue logs (auto-expire after 30 days)
  getCreatorErrors: (params = {}) =>
    api.get('/api/admin/creator-errors', { params }).then(r => r.data),

  purgeCreatorErrors: () =>
    api.delete('/api/admin/creator-errors/purge').then(r => r.data),

  // Outbound email send log — every transactional + campaign email the
  // platform has sent (auto-expires after 90 days)
  getEmailLogs: (params = {}) =>
    api.get('/api/admin/email-logs', { params }).then(r => r.data),

  purgeEmailLogs: () =>
    api.delete('/api/admin/email-logs/purge').then(r => r.data),

    downloadAsset: async (assetId) => {
    const response = await api.get(`/api/admin/assets/${assetId}/download`);
    return response.data;
  },

  // Admin asset full edit
  updateAssetAdmin: async (assetId, formData) => {
    const response = await api.patch(`/api/admin/assets/${assetId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // ============================================
  // SUPPORT TICKETS (ADMIN)
  // ============================================
  
  // Get all support tickets with filters
  getSupportTickets: (params = {}) =>
    api.get('/api/admin/support/tickets', { params }).then(r => r.data),

  // Count of active tickets with an unactioned creator reply (or a brand-new
  // ticket) — for the sidebar badge, distinct from "all open tickets".
  getSupportUnreadCount: () =>
    api.get('/api/admin/support/tickets/unread-count').then(r => r.data),

  // Get single ticket details (uses customer endpoint with admin auth)
  getSupportTicketDetails: (ticketId) =>
    api.get(`/api/support/tickets/${ticketId}`).then(r => r.data),

  // Update ticket (status, priority, assignee, category)
  updateSupportTicket: (ticketId, data) =>
    api.patch(`/api/admin/support/tickets/${ticketId}`, data).then(r => r.data),

  // Add admin reply to ticket (uses customer endpoint with admin auth)
  addTicketReply: (ticketId, data) =>
    api.post(`/api/support/tickets/${ticketId}/messages`, data).then(r => r.data),

  // Upload a screenshot/file to attach to a reply — returns { url, filename, content_type }
  uploadSupportAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/support/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  // Add internal note (admin only)
  addInternalNote: (ticketId, data) =>
    api.post(`/api/admin/support/tickets/${ticketId}/notes`, data).then(r => r.data),

  // Toggle star on ticket
  toggleTicketStar: (ticketId) =>
    api.post(`/api/admin/support/tickets/${ticketId}/star`).then(r => r.data),

  // Bulk update tickets
  bulkUpdateTickets: (data) =>
    api.post('/api/admin/support/tickets/bulk-update', data).then(r => r.data),

  // Get support analytics
  getSupportAnalytics: (days = 30) =>
    api.get('/api/admin/support/analytics', { params: { days } }).then(r => r.data),

  // Get support agents list
  getSupportAgents: () =>
    api.get('/api/admin/support/agents').then(r => r.data),

  // Send auto-response using template
  sendAutoResponse: (ticketId, template) =>
    api.post(`/api/admin/support/tickets/${ticketId}/auto-respond`, { template }).then(r => r.data),

  // Get SLA compliance metrics
  getSLACompliance: (days = 30) =>
    api.get('/api/admin/support/metrics/sla-compliance', { params: { days } }).then(r => r.data),

  // Get response time metrics
  getResponseTimeMetrics: (days = 30) =>
    api.get('/api/admin/support/metrics/response-times', { params: { days } }).then(r => r.data),
  // Send notification to specific users
  sendNotification: (data) =>
    api.post('/api/admin/notifications/send', data),

  // Broadcast to all users
  broadcastNotification: (data) =>
    api.post('/api/admin/notifications/broadcast', data),

  // Send a push notification to specific users
  sendPush: (data) =>
    api.post('/api/admin/push/send', data).then(r => r.data),

  // Broadcast a push notification to all (or active) creators
  broadcastPush: (data) =>
    api.post('/api/admin/push/broadcast', data).then(r => r.data),

  // How many devices a broadcast would actually hit, before sending one.
  getPushReach: () =>
    api.get('/api/admin/push/reach').then(r => r.data),

  // Creator picker for a targeted send, annotated with per-creator device counts
  // so an admin can see who genuinely can't be reached.
  getPushCreators: (params = {}) =>
    api.get('/api/admin/push/creators', { params }).then(r => r.data),

  getPushHistory: () =>
    api.get('/api/admin/push/history').then(r => r.data),

  // Every push the platform sent, automatic ones included — distinct from
  // getPushHistory above, which reads the audit log and so only ever showed
  // admin-initiated sends. Carries the accepted/received/clicked split.
  getPushDeliveries: (params = {}) =>
    api.get('/api/admin/push/deliveries', { params }).then(r => r.data),

  // One creator's push history plus their registered devices — "nothing was
  // sent" and "sent, but they have no device" look identical to the creator
  // and need telling apart.
  getPushDeliveriesForCreator: (creatorId, params = {}) =>
    api.get(`/api/admin/push/deliveries/creator/${creatorId}`, { params }).then(r => r.data),

  // Content-policy moderation warnings — a reusable template (bell + push +
  // email in one send), optionally with the flagged asset deactivated.
  getModerationTemplates: () =>
    api.get('/api/admin/moderation/templates').then(r => r.data),

  sendModerationWarning: (data) =>
    api.post('/api/admin/moderation/send-warning', data).then(r => r.data),

  // Listing reports / IP takedowns — /admin/listing-reports
  getListingReports: (view = 'needs_action') =>
    api.get('/api/admin/listing-reports', { params: { view } }).then(r => r.data),
  getListingReport: (reportId) =>
    api.get(`/api/admin/listing-reports/${reportId}`).then(r => r.data),
  actOnListingReport: (reportId, data) =>
    api.post(`/api/admin/listing-reports/${reportId}/action`, data).then(r => r.data),

  // Reviews creators have reported — /admin/review-reports
  getReviewReports: (status = 'open') =>
    api.get('/api/admin/review-reports', { params: { status } }).then(r => r.data),
  actOnReviewReport: (reviewId, data) =>
    api.post(`/api/admin/review-reports/${reviewId}`, data).then(r => r.data),

  // All reviews + stats + rating-email funnel — /admin/reviews
  getAdminReviews: (params) =>
    api.get('/api/admin/reviews', { params }).then(r => r.data),
  setReviewVisibility: (reviewId, data) =>
    api.post(`/api/admin/reviews/${reviewId}/visibility`, data).then(r => r.data),

  // ── Announcements (product news; first-class, not fanned-out) ──
  listAnnouncements: () =>
    api.get('/api/admin/announcements').then(r => r.data),
  createAnnouncement: (data) =>
    api.post('/api/admin/announcements', data).then(r => r.data),
  updateAnnouncement: (announcementId, data) =>
    api.patch(`/api/admin/announcements/${announcementId}`, data).then(r => r.data),
  deleteAnnouncement: (announcementId) =>
    api.delete(`/api/admin/announcements/${announcementId}`).then(r => r.data),

  // Poster/banner media. Its own presign endpoint (admin-auth) — distinct
  // from every creator-facing upload flow — so it's its own helper rather
  // than reusing src/utils/uploadImage.js, which calls a creator-auth route.
  uploadAnnouncementMedia: async (file) => {
    const presigned = await api.post('/api/admin/announcements/presigned-upload', {
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
    }).then(r => r.data);

    const { upload_url, upload_fields, file_url } = presigned;
    const formData = new FormData();
    Object.keys(upload_fields || {}).forEach((key) => formData.append(key, upload_fields[key]));
    formData.append('file', file); // must be appended last for S3's POST policy

    const uploadResp = await fetch(upload_url, { method: 'POST', body: formData });
    if (!uploadResp.ok) throw new Error('Upload to storage failed');
    return file_url;
  },

  // Weekly summary email (trigger for all / filtered creators)
  sendWeeklySummary: (data = {}) =>
    api.post('/api/admin/emails/weekly-summary', data).then(r => r.data),

  // Custom mail with criteria filters
  sendCustomMail: (data) =>
    api.post('/api/admin/emails/custom', data).then(r => r.data),

  // Preview how many recipients match custom mail filters
  previewCustomMailRecipients: (data) =>
    api.post('/api/admin/emails/preview-recipients', data).then(r => r.data),

  // ── Campaign management ──────────────────────────────────────────────────
  getCampaigns: (params = {}) =>
    api.get('/api/admin/emails/campaigns', { params }).then(r => r.data),
  getCampaign: (id) =>
    api.get(`/api/admin/emails/campaigns/${id}`).then(r => r.data),
  createCampaign: (data) =>
    api.post('/api/admin/emails/campaigns', data).then(r => r.data),
  updateCampaign: (id, data) =>
    api.patch(`/api/admin/emails/campaigns/${id}`, data).then(r => r.data),
  deleteCampaign: (id) =>
    api.delete(`/api/admin/emails/campaigns/${id}`).then(r => r.data),
  sendCampaign: (id, data = {}) =>
    api.post(`/api/admin/emails/campaigns/${id}/send`, data).then(r => r.data),
  getCampaignEvents: (id) =>
    api.get(`/api/admin/emails/campaigns/${id}/events`).then(r => r.data),

  // ── Test send ────────────────────────────────────────────────────────────
  sendTestEmail: (data) =>
    api.post('/api/admin/emails/send-test', data).then(r => r.data),

  // ── Template library ─────────────────────────────────────────────────────
  getEmailTemplates: () =>
    api.get('/api/admin/emails/templates').then(r => r.data),
  createEmailTemplate: (data) =>
    api.post('/api/admin/emails/templates', data).then(r => r.data),
  updateEmailTemplate: (id, data) =>
    api.patch(`/api/admin/emails/templates/${id}`, data).then(r => r.data),
  deleteEmailTemplate: (id) =>
    api.delete(`/api/admin/emails/templates/${id}`).then(r => r.data),

  // Get all creators
  getAllCreators: () =>
    api.get('/api/admin/creators'),

  // ============================================
  // BLOG MANAGEMENT
  // ============================================
  getBlogs: (params = {}) =>
    api.get('/api/admin/blogs', { params }).then(r => r.data),

  getBlog: (id) =>
    api.get(`/api/admin/blogs/${id}`).then(r => r.data),

  createBlog: (data) =>
    api.post('/api/admin/blogs', data).then(r => r.data),

  autoSaveBlog: (data) =>
    api.post('/api/admin/blogs', data).then(r => r.data),

  updateBlog: (id, data) =>
    api.patch(`/api/admin/blogs/${id}`, data).then(r => r.data),

  updateBlogStatus: (id, status) =>
    api.patch(`/api/admin/blogs/${id}/status`, { status }).then(r => r.data),

  deleteBlog: (id) =>
    api.delete(`/api/admin/blogs/${id}`).then(r => r.data),

  // ============================================
  // REFERRALS
  // ============================================
  // Leaderboard of all creators sorted by signup count
  getReferralLeaderboard: (params = {}) =>
    api.get('/api/admin/referrals', { params }).then(r => r.data),

  // Deep-dive: all clicks + signups for one creator
  getCreatorReferralDetail: (creatorId) =>
    api.get(`/api/admin/referrals/${creatorId}`).then(r => r.data),

  // Attribution report: organic vs referred breakdown with by-source counts
  getReferralAttribution: () =>
    api.get('/api/admin/referrals/attribution').then(r => r.data),

  // Commission breakdown for one creator
  getCreatorReferralCommissions: (creatorId) =>
    api.get(`/api/admin/referrals/${creatorId}/commissions`).then(r => r.data),

  // Record a manual payout for one creator
  recordReferralPayout: (creatorId, data) =>
    api.post(`/api/admin/referrals/${creatorId}/payout`, data).then(r => r.data),

  // ============================================
  // SUPPORTERS
  // ============================================
  // All creators using Supporters (currently enabled and/or ever received one), with aggregate stats
  getSupportersLeaderboard: (params = {}) =>
    api.get('/api/admin/supporters', { params }).then(r => r.data),

  // Deep-dive: settings + every raw transaction (amount, fees, buyer contact, message) for one creator
  getCreatorSupporterDetail: (creatorId, params = {}) =>
    api.get(`/api/admin/supporters/${creatorId}`, { params }).then(r => r.data),

  // ============================================
  // INSTAGRAM AUTO-DM
  // ============================================
  getInstagramStats: () =>
    api.get('/api/admin/instagram/stats').then(r => r.data),

  getInstagramActivity: (params = {}) =>
    api.get('/api/admin/instagram/activity', { params }).then(r => r.data),

  getInstagramConnectedCreators: () =>
    api.get('/api/admin/instagram/creators').then(r => r.data),

  // Pilot allow-list for Auto-DM (feature is gated pending Meta App Review of
  // the `comments` permission scope) — enabling gives free access restricted
  // to MintLink-product links, capped at rule_limit active rules (omit/null
  // to fall back to the platform-wide default).
  setInstagramAutodmFlag: (creatorId, { enabled, rule_limit } = {}) =>
    api.patch(`/api/admin/creators/${creatorId}/instagram-autodm-flag`, { enabled, rule_limit }).then(r => r.data),

  // Bulk version — flip pilot access for every creator platform-wide
  // ({ all: true }) or a specific list of creator_ids at once.
  bulkSetInstagramAutodmFlag: ({ all, creator_ids, enabled, rule_limit, notify } = {}) =>
    api.patch('/api/admin/creators/instagram-autodm-flag/bulk', { all, creator_ids, enabled, rule_limit, notify }).then(r => r.data),

  // Manually grant (comp, no charge) or revoke a creator's paid ₹249/mo Auto-DM
  // subscription. Revoke cuts access off immediately (unlike the creator's own
  // self-service cancel, which is cancel-at-period-end).
  manageAutodmSubscription: (creatorId, { action, days } = {}) =>
    api.patch(`/api/admin/creators/${creatorId}/autodm-subscription`, { action, days }).then(r => r.data),

  // Extra Instagram account trials: free add-on slots for N days. Granting
  // also makes sure the Paid plan covers the trial (never shortens it), since
  // a slot does nothing without the plan. Ending expires trial slots only.
  getInstagramAccountTrial: (creatorId) =>
    api.get(`/api/admin/instagram/creators/${creatorId}/account-trial`).then(r => r.data),
  grantInstagramAccountTrial: (creatorId, { accounts, days }) =>
    api.post(`/api/admin/instagram/creators/${creatorId}/account-trial`, { accounts, days }).then(r => r.data),
  endInstagramAccountTrial: (creatorId) =>
    api.delete(`/api/admin/instagram/creators/${creatorId}/account-trial`).then(r => r.data),

  // Every creator with any Auto-DM access grant — pilot-enabled and/or paid
  // (current or past) — the allow-list management view.
  getInstagramAutodmAccessList: (params = {}) =>
    api.get('/api/admin/instagram/autodm-access', { params }).then(r => r.data),

  // Every Auto-DM rule across every creator — moderation view. Rules whose
  // free-text fields contain a non-MintLink link (a bypass of the button-link
  // restriction) come back flagged.
  getAllAutodmRules: (params = {}) =>
    api.get('/api/admin/instagram/rules', { params }).then(r => r.data),

  adminToggleAutodmRule: (creatorId, ruleId) =>
    api.patch(`/api/admin/instagram/rules/${creatorId}/${ruleId}/toggle`).then(r => r.data),

  adminDeleteAutodmRule: (creatorId, ruleId) =>
    api.delete(`/api/admin/instagram/rules/${creatorId}/${ruleId}`).then(r => r.data),

  // ============================================
  // LOCAL LLMs (BETA) — registry of locally-run AI services connected via an
  // ngrok tunnel. Connection records only (name, description, URL, on/off,
  // last connectivity check) — no AI-specific behaviour lives here yet.
  // ============================================
  getLocalLlmServices: () =>
    api.get('/api/admin/local-llms').then(r => r.data),

  createLocalLlmService: ({ name, description } = {}) =>
    api.post('/api/admin/local-llms', { name, description }).then(r => r.data),

  updateLocalLlmService: (serviceId, payload = {}) =>
    api.patch(`/api/admin/local-llms/${serviceId}`, payload).then(r => r.data),

  deleteLocalLlmService: (serviceId) =>
    api.delete(`/api/admin/local-llms/${serviceId}`).then(r => r.data),

  testLocalLlmService: (serviceId) =>
    api.post(`/api/admin/local-llms/${serviceId}/test`).then(r => r.data),

  // ============================================
  // AGENT METRICS — the read-only MCP connection an external agentic AI
  // platform uses to read Mintlink business figures. Per-department credential
  // status, manual issue/revoke/rotate, and the OAuth "Connect" consent screen
  // a partner platform's browser redirect lands on. See
  // MINTLINK-BACKEND/contracts/agent-metrics/v1/ for the full contract.
  // ============================================
  getAgentMetricsOverview: () =>
    api.get('/api/admin/agent-metrics/overview').then(r => r.data),

  issueAgentMetricsCredential: (department) =>
    api.post('/api/admin/agent-metrics/credentials/issue', { department }).then(r => r.data),

  revokeAgentMetricsCredential: (credentialId) =>
    api.post(`/api/admin/agent-metrics/credentials/${credentialId}/revoke`).then(r => r.data),

  rotateAgentMetricsCredential: (credentialId) =>
    api.post(`/api/admin/agent-metrics/credentials/${credentialId}/rotate`).then(r => r.data),

  // `params` are the OAuth query params the partner's "Connect" button redirected
  // with (client_id, redirect_uri, scope, response_type, code_challenge,
  // code_challenge_method) — forwarded as-is, this admin never constructs them.
  getAgentMetricsConsent: (params) =>
    api.get('/api/admin/agent-metrics/oauth/authorize', { params }).then(r => r.data),

  decideAgentMetricsConsent: (payload) =>
    api.post('/api/admin/agent-metrics/oauth/authorize', payload).then(r => r.data),

  // AI apps (Claude, ChatGPT, DeltaAI, ...) connected to the admin MCP through
  // mintlink.in/connect/authorize — one row per connection, one department each.
  getAdminMcpConnections: () =>
    api.get('/api/admin/mcp-connections').then(r => r.data),

  revokeAdminMcpConnection: (connectionId) =>
    api.post(`/api/admin/mcp-connections/${encodeURIComponent(connectionId)}/revoke`).then(r => r.data),

  // ============================================
  // INSTAGRAM POST SCHEDULER (internal — MintLink's own account)
  // ============================================
  getSchedulerConnectUrl: () =>
    api.get('/api/admin/instagram/scheduler/connect').then(r => r.data),

  getSchedulerStatus: () =>
    api.get('/api/admin/instagram/scheduler/status').then(r => r.data),

  disconnectScheduler: () =>
    api.delete('/api/admin/instagram/scheduler/disconnect').then(r => r.data),

  getScheduledPosts: () =>
    api.get('/api/admin/instagram/scheduler/posts').then(r => r.data),

  // scheduledForUtcIso: UTC ISO string with no trailing 'Z' (matches backend's get_timestamp())
  createScheduledPost: ({ image, caption, scheduledForUtcIso }) => {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('caption', caption || '');
    formData.append('scheduled_for', scheduledForUtcIso);
    return api.post('/api/admin/instagram/scheduler/posts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  deleteScheduledPost: (postId) =>
    api.delete(`/api/admin/instagram/scheduler/posts/${postId}`).then(r => r.data),

  publishScheduledPostNow: (postId) =>
    api.post(`/api/admin/instagram/scheduler/posts/${postId}/publish-now`).then(r => r.data),

  // ============================================
  // SOCIAL CALENDAR (internal — MintLink's own Instagram, X, LinkedIn, YouTube)
  // See app/routes/admin/social_calendar.py.
  // ============================================
  getSocialAccounts: () =>
    api.get('/api/admin/social/calendar/accounts').then(r => r.data),

  connectSocialAccount: (network) =>
    api.post(`/api/admin/social/calendar/accounts/${network}/connect`).then(r => r.data),

  disconnectSocialAccount: (network) =>
    api.delete(`/api/admin/social/calendar/accounts/${network}`).then(r => r.data),

  socialPresignMedia: (body) =>
    api.post('/api/admin/social/calendar/media/presign', body).then(r => r.data),

  socialImportMediaUrl: (url) =>
    api.post('/api/admin/social/calendar/media/import-url', { url }).then(r => r.data),

  getSocialPosts: (params = {}) =>
    api.get('/api/admin/social/calendar/posts', { params }).then(r => r.data),

  createSocialPost: (body) =>
    api.post('/api/admin/social/calendar/posts', body).then(r => r.data),

  updateSocialPost: (postId, body) =>
    api.put(`/api/admin/social/calendar/posts/${postId}`, body).then(r => r.data),

  // scheduledFor: UTC ISO, or null to move it back to drafts.
  rescheduleSocialPost: (postId, scheduledFor) =>
    api.patch(`/api/admin/social/calendar/posts/${postId}/schedule`, { scheduled_for: scheduledFor }).then(r => r.data),

  publishSocialPostNow: (postId) =>
    api.post(`/api/admin/social/calendar/posts/${postId}/publish-now`).then(r => r.data),

  retrySocialPost: (postId) =>
    api.post(`/api/admin/social/calendar/posts/${postId}/retry`).then(r => r.data),

  duplicateSocialPost: (postId) =>
    api.post(`/api/admin/social/calendar/posts/${postId}/duplicate`).then(r => r.data),

  deleteSocialPost: (postId) =>
    api.delete(`/api/admin/social/calendar/posts/${postId}`).then(r => r.data),

  bulkCreateSocialPosts: (posts) =>
    api.post('/api/admin/social/calendar/posts/bulk', { posts }).then(r => r.data),

  autoScheduleSocialDrafts: (postIds) =>
    api.post('/api/admin/social/calendar/posts/auto-schedule', { post_ids: postIds }).then(r => r.data),

  setSocialPublishingPaused: (paused) =>
    api.post('/api/admin/social/calendar/pause', { paused }).then(r => r.data),

  // What actually went out on Instagram in a range, incl. posts made in the app.
  getSocialHistory: (params) =>
    api.get('/api/admin/social/calendar/history', { params }).then(r => r.data),

  getSocialBestTimes: () =>
    api.get('/api/admin/social/calendar/best-times').then(r => r.data),

  refreshSocialBestTimes: () =>
    api.post('/api/admin/social/calendar/best-times/refresh').then(r => r.data),

  suggestSocialTimes: (count = 3) =>
    api.get('/api/admin/social/calendar/best-times/suggest', { params: { count } }).then(r => r.data),

  // ============================================
  // ROLE-BASED ACCESS CONTROL
  // ============================================
  getMyAccess: () => api.get('/api/admin/rbac/me').then(r => r.data),

  getRbacModules: () => api.get('/api/admin/rbac/modules').then(r => r.data),

  getRoles: () => api.get('/api/admin/rbac/roles').then(r => r.data),

  createRole: (data) => api.post('/api/admin/rbac/roles', data).then(r => r.data),

  updateRole: (roleId, data) => api.put(`/api/admin/rbac/roles/${roleId}`, data).then(r => r.data),

  deleteRole: (roleId) => api.delete(`/api/admin/rbac/roles/${roleId}`).then(r => r.data),

  getAdmins: () => api.get('/api/admin/rbac/admins').then(r => r.data),

  addAdmin: (data) => api.post('/api/admin/rbac/admins', data).then(r => r.data),

  updateAdminRole: (uid, roleId) =>
    api.put(`/api/admin/rbac/admins/${uid}`, { role_id: roleId }).then(r => r.data),

  removeAdmin: (uid) => api.delete(`/api/admin/rbac/admins/${uid}`).then(r => r.data),

  // ============================================
  // MINTLINK PLUS
  // ============================================
  getPlusSubscribers: (params = {}) =>
    api.get('/api/admin/plus/subscribers', { params }).then(r => r.data),

  getPlusSubscriberDetail: (creatorId) =>
    api.get(`/api/admin/plus/subscribers/${creatorId}`).then(r => r.data),

  searchCreatorsForPlus: (q) =>
    api.get('/api/admin/plus/creators/search', { params: { q } }).then(r => r.data),

  grantPlusTrial: (creatorId, days, note) =>
    api.post(`/api/admin/plus/subscribers/${creatorId}/grant-trial`, { days, note }).then(r => r.data),

  revokePlusAccess: (creatorId) =>
    api.post(`/api/admin/plus/subscribers/${creatorId}/revoke`).then(r => r.data),

  // The activation funnel: five counts, four gaps, and the four cuts that
  // sharpen it. Reads the whole table, so it is loaded on demand — never polled.
  getFunnel: (params = {}) =>
    api.get('/api/admin/funnel', { params }).then(r => r.data),

  // Every website built with the storefront builder, with its trial state.
  // `status` is one of: none | active | expired | converted | live | draft.
  getWebsites: (params = {}) =>
    api.get('/api/admin/websites', { params }).then(r => r.data),

  // ============================================
  // REVIEW MY WORK — ORDERS & CREATOR OVERSIGHT
  // ============================================
  getReviewOrders: (params = {}) =>
    api.get('/api/admin/review-orders', { params }).then(r => r.data),

  getReviewOrder: (orderId) =>
    api.get(`/api/admin/review-orders/${orderId}`).then(r => r.data),

  updateReviewOrder: (orderId, data) =>
    api.patch(`/api/admin/review-orders/${orderId}`, data).then(r => r.data),

  forceRefundReviewOrder: (orderId, reason) =>
    api.post(`/api/admin/review-orders/${orderId}/force-refund`, { reason }).then(r => r.data),

  extendReviewOrderDeadline: (orderId, additionalHours) =>
    api.post(`/api/admin/review-orders/${orderId}/extend-deadline`, { additional_hours: additionalHours }).then(r => r.data),

  uploadReviewOnBehalf: (orderId, data) =>
    api.post(`/api/admin/review-orders/${orderId}/upload-on-behalf`, data).then(r => r.data),

  deleteReviewOrderFile: (orderId, fileId) =>
    api.delete(`/api/admin/review-orders/${orderId}/files/${fileId}`).then(r => r.data),

  getReviewOrderStats: () =>
    api.get('/api/admin/review-orders/stats').then(r => r.data),

  getLateReviewCreators: () =>
    api.get('/api/admin/review-orders/late-creators').then(r => r.data),

  suspendCreatorReviewServices: (creatorId, reason) =>
    api.post(`/api/admin/creators/${creatorId}/suspend-review-services`, { reason }).then(r => r.data),

  reinstateCreatorReviewServices: (creatorId) =>
    api.post(`/api/admin/creators/${creatorId}/reinstate-review-services`).then(r => r.data),

  getUploadScans: (params = {}) =>
    api.get('/api/admin/upload-scans', { params }).then(r => r.data),

  // Review My Work — SERVICE LISTINGS (distinct from orders above)
  getReviewServices: (params = {}) =>
    api.get('/api/admin/review-services', { params }).then(r => r.data),

  getReviewService: (serviceId) =>
    api.get(`/api/admin/review-services/${serviceId}`).then(r => r.data),

  // ============================================
  // SERVICES (freelance marketplace) — ORDERS & SELLER OVERSIGHT
  // Distinct from Review My Work above — see app/routes/admin/gigs.py's
  // module docstring for why the two panels are kept fully separate.
  // ============================================
  getGigOrders: (params = {}) =>
    api.get('/api/admin/gig-orders', { params }).then(r => r.data),

  getGigOrder: (orderId) =>
    api.get(`/api/admin/gig-orders/${orderId}`).then(r => r.data),

  updateGigOrder: (orderId, data) =>
    api.patch(`/api/admin/gig-orders/${orderId}`, data).then(r => r.data),

  forceRefundGigOrder: (orderId, reason) =>
    api.post(`/api/admin/gig-orders/${orderId}/force-refund`, { reason }).then(r => r.data),

  extendGigOrderDeadline: (orderId, additionalHours) =>
    api.post(`/api/admin/gig-orders/${orderId}/extend-deadline`, { additional_hours: additionalHours }).then(r => r.data),

  deleteGigOrderFile: (orderId, fileId) =>
    api.delete(`/api/admin/gig-orders/${orderId}/files/${fileId}`).then(r => r.data),

  getGigOrderStats: () =>
    api.get('/api/admin/gig-orders/stats').then(r => r.data),

  getLateGigSellers: () =>
    api.get('/api/admin/gig-orders/late-sellers').then(r => r.data),

  suspendCreatorGigSelling: (creatorId, reason) =>
    api.post(`/api/admin/creators/${creatorId}/suspend-gig-selling`, { reason }).then(r => r.data),

  reinstateCreatorGigSelling: (creatorId) =>
    api.post(`/api/admin/creators/${creatorId}/reinstate-gig-selling`).then(r => r.data),

  // Services — LISTINGS (distinct from orders above)
  getGigs: (params = {}) =>
    api.get('/api/admin/gigs', { params }).then(r => r.data),

  getGig: (gigId) =>
    api.get(`/api/admin/gigs/${gigId}`).then(r => r.data),

  // Community moderation. Note that unmaskCommunityMember is the ONLY call
  // here that returns a real identity, and every use of it writes an audit
  // record — so don't call it to decorate a list, only when a moderator has
  // deliberately asked who someone is.
  getCommunityOverview: () =>
    api.get('/api/admin/community/overview').then(r => r.data),

  // Posts AND replies on one timeline, plus a zero-filled daily series —
  // "is anyone talking, and is it slowing down". Replies are only in here
  // because comments now carry a global index attribute; see the backend.
  getCommunityActivity: (params = {}) =>
    api.get('/api/admin/community/activity', { params }).then(r => r.data),

  // The member directory. Handles only, like every other community list —
  // putting a name next to a row is still unmaskCommunityMember below.
  // sort is recent|posts|karma|strikes.
  getCommunityMembers: (params = {}) =>
    api.get('/api/admin/community/members', { params }).then(r => r.data),

  // The reverse of unmask: creator -> handle, for when support already knows
  // who emailed them. Audited identically, because it discloses the same link.
  whoisCommunityCreator: (creatorId) =>
    api.get('/api/admin/community/whois', { params: { creator_id: creatorId } }).then(r => r.data),

  getCommunityReports: (status = 'open') =>
    api.get('/api/admin/community/reports', { params: { status } }).then(r => r.data),

  decideCommunityReport: (reportId, payload) =>
    api.post(`/api/admin/community/reports/${reportId}/decide`, payload).then(r => r.data),

  getCommunityMember: (handle) =>
    api.get(`/api/admin/community/members/${handle}`).then(r => r.data),

  sanctionCommunityMember: (handle, payload) =>
    api.post(`/api/admin/community/members/${handle}/sanction`, payload).then(r => r.data),

  unmaskCommunityMember: (handle) =>
    api.post(`/api/admin/community/members/${handle}/unmask`).then(r => r.data),

  updateCommunityPost: (postId, payload) =>
    api.patch(`/api/admin/community/posts/${postId}`, payload).then(r => r.data),

  // Refile a post under a different topic — the proportionate answer to a
  // product plug in a help topic, where removing it would be too much.
  moveCommunityPostTopic: (postId, topic) =>
    api.patch(`/api/admin/community/posts/${postId}/topic`, { topic }).then(r => r.data),

  // Monitoring: everything, not just what somebody reported. status is
  // live|hidden|removed|all.
  getCommunityPosts: (params = {}) =>
    api.get('/api/admin/community/posts', { params }).then(r => r.data),

  // The moderator's view of a thread — includes removed comments and the
  // private not-helpful counts that members never see.
  getCommunityThread: (postId) =>
    api.get(`/api/admin/community/posts/${postId}`).then(r => r.data),

  // Posts as the shared MintLinkTeam handle, not as the individual admin.
  createCommunityPost: (payload) =>
    api.post('/api/admin/community/posts', payload).then(r => r.data),

  replyToCommunityPost: (postId, payload) =>
    api.post(`/api/admin/community/posts/${postId}/reply`, payload).then(r => r.data),

  removeCommunityComment: (postId, commentId, { reason, strike } = {}) =>
    api.delete(`/api/admin/community/posts/${postId}/comments/${commentId}`,
      { params: { reason, strike } }).then(r => r.data),

  // Takes the whole thread down. Reversible via restoreCommunityPost — the
  // post is marked removed, not deleted, so its replies and reports survive.
  removeCommunityPost: (postId, { reason, strike } = {}) =>
    api.delete(`/api/admin/community/posts/${postId}`,
      { params: { reason, strike } }).then(r => r.data),

  restoreCommunityPost: (postId) =>
    api.post(`/api/admin/community/posts/${postId}/restore`).then(r => r.data),

  // ============================================
  // GROWTH PLANNER (internal — see app/routes/admin/planner.py)
  // ============================================
  getPlans: (params = {}) =>
    api.get('/api/admin/planner/plans', { params }).then(r => r.data),

  getPlan: (planId) =>
    api.get(`/api/admin/planner/plans/${planId}`).then(r => r.data),

  createPlan: (payload) =>
    api.post('/api/admin/planner/plans', payload).then(r => r.data),

  updatePlan: (planId, payload) =>
    api.patch(`/api/admin/planner/plans/${planId}`, payload).then(r => r.data),

  deletePlan: (planId) =>
    api.delete(`/api/admin/planner/plans/${planId}`).then(r => r.data),

  // Mainly for rolling a quarter forward without retyping the document.
  duplicatePlan: (planId) =>
    api.post(`/api/admin/planner/plans/${planId}/duplicate`).then(r => r.data),

  // The growth scorecard a plan is written against. Two full table scans on the
  // backend, so this is deliberately on-demand — load it on mount or a refresh
  // button, never on every keystroke or render.
  getPlannerMetrics: (cohortMonths = 12) =>
    api.get('/api/admin/planner/metrics', {
      params: { cohort_months: cohortMonths },
      timeout: 120000,
    }).then(r => r.data),

  // The named creators behind the activation numbers, for an outreach drive.
  // One table scan (activation is denormalised onto the creator row), but still
  // a scan — on-demand behind a button, same as the scorecard above.
  // segment: all | listed | never
  getActivationSegments: ({ segment = 'all', includeInactive = false } = {}) =>
    api.get('/api/admin/planner/activation-segments', {
      params: { segment, include_inactive: includeInactive },
      timeout: 120000,
    }).then(r => r.data),

  // ── Task tracker ──────────────────────────────────────────────────────────
  // Answers with { tasks, closed, closed_total, closed_complete, counts,
  // overdue, today }. `tasks` is every OPEN task matching the filters; `closed`
  // is a capped page of finished ones, newest first, because finished work is
  // the half that grows without limit. Pass include_closed + closed_limit +
  // closed_offset to page through it — the tracker only does so when somebody
  // opens the collapsed Done drawer.
  //
  // Rows are list-shaped: descriptions clamped to a preview, checklists reduced
  // to counts, activity trails omitted entirely (they gain an entry per edit and
  // the list draws none of them). Use getPlannerTask for the full record.
  //
  // `status` accepts a comma-separated set. `counts` is deliberately computed
  // across every filter EXCEPT status, so clicking through statuses does not
  // rewrite the tile telling you how many are done.
  getPlannerTasks: (params = {}) =>
    api.get('/api/admin/planner/tasks', { params }).then(r => r.data),

  // The complete task — full description, checklist items, activity trail. One
  // GetItem, fetched when a task is actually opened.
  getPlannerTask: (taskId) =>
    api.get(`/api/admin/planner/tasks/${taskId}`).then(r => r.data),

  // Resolves with { task, row, notification }. `notification` is a delivery
  // report — targets reached, devices pushed, and who could NOT be reached.
  // Surface it: an admin with no registered device gets nothing, and silently
  // pretending otherwise is how a task quietly goes unnoticed.
  createPlannerTask: (payload) =>
    api.post('/api/admin/planner/tasks', payload).then(r => r.data),

  // Also resolves with `row` — the same task in list shape. Splice that into the
  // list already held instead of reloading every task to learn about the one
  // just written; that is what makes a status flip feel instant.
  updatePlannerTask: (taskId, payload) =>
    api.patch(`/api/admin/planner/tasks/${taskId}`, payload).then(r => r.data),

  deletePlannerTask: (taskId) =>
    api.delete(`/api/admin/planner/tasks/${taskId}`).then(r => r.data),

  // Super admins, each with a push_devices count so the UI can warn before
  // someone assigns work believing a notification went out.
  getPlannerAssignees: () =>
    api.get('/api/admin/planner/assignees').then(r => r.data),

  // ============================================
  // GST / SELLER FEE INVOICES (fee revision, 28 Aug 2026)
  //
  // The readiness dashboard reads one endpoint (getInvoiceReadiness) that
  // recomputes who is where — pending generation, pending send, sent — from
  // sales and invoice records on every call, rather than a cached list. See
  // app/routes/admin/tax_ledger.py for why: a stale eligibility list is worse
  // than a slow one on a document this legally load-bearing.
  // ============================================
  getInvoiceReadiness: (period) =>
    api.get('/api/admin/tax/invoices/status', { params: { period } }).then(r => r.data),

  generateInvoicesForPeriod: (period) =>
    api.post('/api/admin/tax/invoices/generate', null, { params: { period } }).then(r => r.data),

  // Sends the next `limit` pending invoices for the period and reports how
  // many are left — the panel calls this in a loop to drive a progress bar,
  // since a platform-wide month is too many emails for one request (see the
  // backend docstring for why this isn't a single "send everything" call).
  sendInvoiceBatch: (period, limit = 25) =>
    api.post('/api/admin/tax/invoices/send', null, { params: { period, limit } }).then(r => r.data),

  // A single resend, e.g. a creator says they never got theirs.
  resendInvoice: (period, creatorId) =>
    api.post('/api/admin/tax/invoices/send', null, {
      params: { period, creator_ids: creatorId },
    }).then(r => r.data),

  getIssuedInvoices: (period) =>
    api.get('/api/admin/tax/invoices', { params: { period } }).then(r => r.data),

  getTaxLedger: (params = {}) =>
    api.get('/api/admin/tax/ledger', { params }).then(r => r.data),

  getGstr1: (period) =>
    api.get('/api/admin/tax/gstr1', { params: { period } }).then(r => r.data),

  // Costs MintLink bore itself on sales BEFORE the 28 Aug 2026 fee cutover —
  // the window seller fee invoices don't cover. Purpose-built to stay cheap
  // (date window in the query key, no per-sale enrichment); safe to call for
  // any period, a non-overlapping one comes back zeroed.
  getPreCutoverBorne: (period) =>
    api.get('/api/admin/tax/pre-cutover-borne', { params: { period } }).then(r => r.data),

  reconcileTaxPeriod: (period) =>
    api.get('/api/admin/tax/reconcile', { params: { period } }).then(r => r.data),

  // Subscription invoices (Instagram Automation): one per payment, issued at
  // payment time on the same ML/ sequence. `missing` = paid with no invoice.
  getSubscriptionInvoices: (period) =>
    api.get('/api/admin/tax/subscription-invoices', { params: { period } }).then(r => r.data),

  issueMissingSubscriptionInvoices: (period) =>
    api.post('/api/admin/tax/subscription-invoices/issue-missing', null, { params: { period } }).then(r => r.data),

  // The invoice document as HTML (auth header required, so fetched rather
  // than linked).
  getSubscriptionInvoiceHtml: (creatorId, txnid) =>
    api.get(`/api/admin/tax/subscription-invoices/${encodeURIComponent(creatorId)}/${encodeURIComponent(txnid)}`,
      { responseType: 'text' }).then(r => r.data),
};